import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnChanges,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { getUVEState } from '@dotcms/uve';
import { Observable, combineLatest, map } from 'rxjs';

import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';
import { whenTruthy } from 'ish-core/utils/operators';

import { DotCmsAnimationComponent } from '../../animations/animation/dot-cms-animation.component';
import { getFieldAnimation } from '../../animations/dot-cms-aniamtion.helper';
import { DotCmsUveFacade } from '../../facades/dot-cms-uve.facade';
import { ImageContentlet } from '../../models/dot-cms-uve-contentlet.model';
import { BlockEditorContent, BlockEditorNode } from '../../models/dotcms-block-editor.model';
import { DotCmsCodecEncodePipe } from '../../pipes/dot-cms-codec-encode.pipe';

@Component({
  selector: 'custom-dot-cms-image',
  templateUrl: './dot-cms-uve-image.component.html',
  styleUrls: ['./dot-cms-uve-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, NgClass, NgStyle, AsyncPipe, NgTemplateOutlet, DotCmsCodecEncodePipe, DotCmsAnimationComponent],
})
export class DotCmsUveImageComponent implements OnChanges {
  content = input<BlockEditorContent>();
  attrs = input<BlockEditorNode['attrs']>();
  contentlet = input<ImageContentlet>();
  isInSideEditor: boolean;

  private readonly dotCmsUveFacade = inject(DotCmsUveFacade);
  private readonly elementRef = inject(ElementRef);
  private readonly gtmFacade = inject(GoogleTagManagerFacade);

  readonly hosts = signal<string>('');
  readonly host$: Observable<string> = this.dotCmsUveFacade.getDotCmsHost$;
  readonly $srcURL = computed(() => {
    const host = this.hosts();
    //@ts-ignore
    const src = this.content()?.attrs?.src;
    return src ? `${host}${src}` : '';
  });

  getFieldAnimation = getFieldAnimation;
  isElementCreated = true;
  triggerCount = 1;
  ngOnChanges(): void {
    this.isInSideEditor = !!getUVEState();

    this.gtmFacade.setupIntersectionObserverForGTMEvent(
      this.elementRef.nativeElement,
      [this.contentlet()],
      this.triggerCount,
      'view_promotion'
    );
  }
  buildImageSrc(): Observable<string> {
    return combineLatest([this.host$.pipe(whenTruthy())]).pipe(
      whenTruthy(),
      map(([host]) => {
        this.hosts.set(host);

        const fileAsset = this.contentlet()?.fileAsset;
        const idPath = fileAsset?.idPath ?? '';

        if (idPath.startsWith('/dA/')) {
          if (this.isInSideEditor) {
            return `${host}${idPath}?publish=${this.contentlet()?.publishDate}`;
          }
          return `${host}${idPath}`;
        }

        const pathParts = idPath.split('/');
        if (this.contentlet()) {
          const relativePath = pathParts.slice(6).join('/');
          return `${host}/dA/${this.contentlet().inode}/${relativePath}`;
        }

        return `${host}${idPath}`;
      })
    );
  }

  resetImageComponentStructure(identifier: string) {
    const imageContainer = document.getElementById(`image-container-${identifier}`);
    const imageElement = document.getElementById(`image-${identifier}`);

    if (imageElement?.querySelectorAll('.image-1')) {
      imageElement.removeAttribute('style');
      imageElement.classList.remove('image-1');
    }

    if (imageContainer && imageElement) {
      while (imageContainer.firstChild) {
        imageContainer.removeChild(imageContainer.firstChild);
      }
      imageContainer.appendChild(imageElement);
    }

    this.isElementCreated = true;
  }
}
